import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type GastosACubrir, type ListadoLlamados, type LlamadoDetalle, type VistaPreviaLlamado } from "@/lib/api";
import {
  aPayloadLlamado,
  formLlamado,
  montoConPropuesta,
  montoPropuesto,
  queryGastosACubrir,
  queryVistaPrevia,
  validarLlamado,
  type ErroresLlamado,
  type FormLlamado,
} from "@/lib/aportes";

const SIN_ERRORES: ErroresLlamado = {};

/** Vista previa del reparto con debounce: el backend reparte, el frontend sólo muestra. */
export function useVistaPrevia(base: string, form: FormLlamado, activa: boolean, llamadoId?: number) {
  const [query, setQuery] = useState<string | null>(null);
  useEffect(() => {
    // Al cerrar (o antes de abrir) no hay query vigente: si no se resetea acá, al reabrir con el
    // formulario en blanco `activa` vuelve a ser true con el query viejo todavía en el estado, y
    // se llega a pedir (o a mostrar, ver más abajo) el reparto de la sesión anterior.
    if (!activa) {
      setQuery(null);
      return;
    }
    const q = queryVistaPrevia(form, llamadoId);
    const timer = setTimeout(() => setQuery(q), 300);
    return () => clearTimeout(timer);
  }, [form, activa, llamadoId]);
  const recurso = useApiResource<VistaPreviaLlamado>(activa && query ? `${base}/vista-previa${query}` : null);
  // useApiResource no limpia `data`/`error` cuando la ruta vuelve a null (por diseño: sirve de caché
  // mientras falta un parámetro). Acá la ruta pasa a null también al cerrar el diálogo o mientras el
  // formulario está incompleto, así que sin `query` vigente no hay que exponer la respuesta vieja.
  return {
    vistaPrevia: query ? (recurso.data ?? null) : null,
    errorVistaPrevia: query ? (recurso.error?.message ?? null) : null,
  };
}

/** Propuesta del monto (Mati, 2026-10-10): lo que falta pagar de los gastos hasta `hasta`. Llena el monto mientras el
 * admin no lo corrija; "Usar" lo vuelve a poner. Como en la vista previa, cerrado no expone la respuesta vieja. */
function usePropuestaMonto(base: string, hasta: string, activa: boolean, monto: string, ponerMonto: (m: string) => void) {
  const query = activa ? queryGastosACubrir(hasta) : null;
  const recurso = useApiResource<GastosACubrir>(query ? `${base}/gastos-a-cubrir${query}` : null);
  const propuesta = query && recurso.data?.hasta === hasta ? recurso.data : null;
  const nueva = montoPropuesto(propuesta);
  const anterior = useRef<string | null>(null);
  useEffect(() => {
    if (!activa) {
      anterior.current = null;
      return;
    }
    if (!propuesta) return; // cargando: no se borra lo propuesto antes de tener la respuesta nueva
    const siguiente = montoConPropuesta(monto, anterior.current, nueva);
    anterior.current = nueva;
    if (siguiente !== monto) ponerMonto(siguiente);
    // Sólo cuando llega otra propuesta: si el admin corrige el monto, no se pisa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propuesta, activa]);
  return {
    propuesta,
    errorPropuesta: query ? (recurso.error?.message ?? null) : null,
    usarPropuesta: nueva === null ? null : () => ponerMonto(nueva),
  };
}

/** Lógica del listado de llamados (sin JSX). Admin emite; el contador sólo mira. */
export function useLlamadosTab() {
  const { fid, membresia } = useContextoApp();
  const navegar = useNavigate();
  const base = `/api/fideicomisos/${fid}/llamados`;
  const esAdmin = membresia.rol === "admin";

  const listado = useApiResource<ListadoLlamados>(base);
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<FormLlamado>(() => formLlamado());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionGuardar = useAsyncAction((datos: ReturnType<typeof aPayloadLlamado>) => api.post<LlamadoDetalle>(base, datos));
  const errores = validarLlamado(form);
  const { vistaPrevia, errorVistaPrevia } = useVistaPrevia(base, form, abierto && esAdmin);
  const [hasta, setHasta] = useState("");
  const ponerMonto = (monto_usd: string) => setForm((f) => ({ ...f, monto_usd }));
  const { propuesta, errorPropuesta, usarPropuesta } = usePropuestaMonto(base, hasta, abierto && esAdmin, form.monto_usd, ponerMonto);

  const abrirNuevo = () => {
    const nuevo = formLlamado();
    setForm(nuevo);
    setHasta(nuevo.vencimiento); // por defecto, lo que hay que pagar hasta que vence el llamado
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setAbierto(true);
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(errores).length > 0) return;
    const creado = await accionGuardar.run(aPayloadLlamado(form, vistaPrevia?.cubierto_con_ventas_usd));
    if (creado) {
      setAbierto(false);
      void navegar(`/llamados/${creado.id}`);
    }
  };

  return {
    esAdmin,
    items: listado.data?.items ?? [],
    totales: listado.data?.totales ?? null,
    cargando: listado.loading && !listado.data,
    errorCarga: listado.data ? null : (listado.error?.message ?? null),
    reintentar: listado.recargar,
    abierto,
    cerrar: () => setAbierto(false),
    abrirNuevo,
    form,
    cambiar: <K extends keyof FormLlamado>(campo: K, valor: FormLlamado[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    vistaPrevia,
    errorVistaPrevia,
    propuesta: { hasta, cambiarHasta: setHasta, datos: propuesta, error: errorPropuesta, usar: usarPropuesta },
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    verFicha: (id: number) => void navegar(`/llamados/${id}`),
    armarPrevision: () => void navegar("/pedido"),
  };
}
